import { Fragment, ReactNode, useState } from 'react';

import { c } from 'ttag';

import {
    Badge,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';
import { EyeOutline } from '@freelancelabs/icons';

import { WRAPPING_HEADER } from '../../components/ListCard';
import { ActivityLogQuery } from '../../graphql/__generated__/types';
import { describeAction } from './actionCatalog';

export type ActivityLogRow = NonNullable<NonNullable<ActivityLogQuery['activityLog']>['items']>[number];

interface Props {
    rows: ActivityLogRow[];
    loading: boolean;
    hasError: boolean;
    state?: ReactNode;
}

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
const TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

const whenCell = (iso: string | null) => {
    if (!iso) {
        return <span className="text-text-400">—</span>;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return <span className="font-mono text-xs text-text-500">{iso}</span>;
    }
    return (
        <span className="whitespace-nowrap">
            <span className="font-medium text-text-900">{DATE_FORMAT.format(date)}</span>
            <span className="block text-[11.5px] tabular-nums text-text-400">{TIME_FORMAT.format(date)}</span>
        </span>
    );
};

const initialsOf = (name: string) => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return '';
    }
    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
    }
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const actorCell = (row: ActivityLogRow) => {
    const isUser = row.actorType === 'user';
    const name = isUser ? (row.actorName ?? c('Activity log').t`Removed user`) : c('Activity log').t`System`;
    return (
        <span className="flex items-center gap-2.5">
            <span
                className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-text-100 text-[11.5px] font-bold text-text-600"
                aria-hidden="true"
            >
                {isUser ? initialsOf(name) : '⚙'}
            </span>
            <span className="min-w-0">
                <span className="block truncate font-semibold text-text-900">{name}</span>
                <span className="block text-[11.5px] text-text-400">
                    {isUser ? c('Activity log').t`Operator` : c('Activity log').t`Automated`}
                </span>
            </span>
        </span>
    );
};

const MetadataDetail = ({ metadata }: { metadata: string | null }) => {
    let parsed: unknown = null;
    if (metadata) {
        try {
            parsed = JSON.parse(metadata);
        } catch {
            return <p className="break-words font-mono text-[11.5px] text-text-700">{metadata}</p>;
        }
    }

    const entries =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? Object.entries(parsed as Record<string, unknown>)
            : [];

    if (entries.length === 0) {
        return <p className="text-xs text-text-500">{c('Activity log').t`No detail recorded for this event.`}</p>;
    }

    return (
        <dl className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-x-6 gap-y-2.5">
            {entries.map(([key, value]) => {
                const isComposite = value !== null && typeof value === 'object';
                const text = value === null ? '—' : isComposite ? JSON.stringify(value) : String(value);
                return (
                    <div key={key} className="min-w-0">
                        <dt className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-text-400">{key}</dt>
                        <dd
                            className={
                                isComposite
                                    ? 'mt-0.5 break-words font-mono text-[11.5px] text-text-700'
                                    : 'mt-0.5 break-words text-[13px] font-medium text-text-900'
                            }
                        >
                            {text}
                        </dd>
                    </div>
                );
            })}
        </dl>
    );
};

const subjectTypeLabel = (subjectType: string | null): string => {
    switch (subjectType) {
        case 'organization':
            return c('Activity log subject').t`Account`;
        case 'membership':
            return c('Activity log subject').t`Membership`;
        case 'dashboard':
            return c('Activity log subject').t`Dashboard`;
        case 'partner_list':
            return c('Activity log subject').t`Partner list`;
        case 'user':
            return c('Activity log subject').t`User`;
        case 'role':
            return c('Activity log subject').t`Role`;
        case 'need_submission':
            return c('Activity log subject').t`Need request`;
        case 'collaborator':
            return c('Activity log subject').t`Collaborator`;
        case 'contact':
            return c('Activity log subject').t`Contact`;
        case 'module':
            return c('Activity log subject').t`Module`;
        default:
            return subjectType ?? c('Activity log subject').t`Object`;
    }
};

const JournalTable = ({ rows, loading, hasError, state }: Props) => {
    const [openId, setOpenId] = useState<string | null>(null);
    const wrapping = '!whitespace-normal !break-words py-2 align-top';

    // Build columns inside the component, not at module level: `c().t` at import would
    // freeze the headers in English before the French locale is loaded.
    const columns: TableHeaderCellProps[] = (
        [
            { id: 'when', label: c('Activity log column').t`When`, minWidth: 132 },
            { id: 'actor', label: c('Activity log column').t`Actor`, minWidth: 200 },
            { id: 'action', label: c('Activity log column').t`Action`, minWidth: 220 },
            { id: 'subject', label: c('Activity log column').t`Object`, minWidth: 190 },
            { id: 'org', label: c('Activity log column').t`Account`, minWidth: 180 },
            { id: 'detail', label: '', minWidth: 56, textAlign: 'end' },
        ] as TableHeaderCellProps[]
    ).map((column) => ({ ...column, className: WRAPPING_HEADER }));

    const columnsHead = <TableHeader cells={columns} />;

    return (
        <>
            <Table className="text-[13px]">
                {columnsHead}
                <TableBody loading={loading} loadingRows={6} loadingColumns={columns.length}>
                    {(hasError ? [] : rows).map((entry) => {
                        const action = entry.action ?? '';
                        const { badgeCategory, label } = describeAction(action);
                        const isOpen = openId === entry.id;
                        const detailLabel = c('Activity log').t`View the detail`;
                        return (
                            <Fragment key={entry.id ?? Math.random()}>
                                <TableRow>
                                    <TableCell className={wrapping}>{whenCell(entry.occurredAt)}</TableCell>
                                    <TableCell className={wrapping}>{actorCell(entry)}</TableCell>
                                    <TableCell className={wrapping}>
                                        {action ? (
                                            <>
                                                <Badge category={badgeCategory} size="small">
                                                    {label}
                                                </Badge>
                                                <span className="mt-1 block font-mono text-[10.5px] text-text-400">
                                                    {action}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="text-text-400">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className={wrapping}>
                                        <span className="block font-medium text-text-900">
                                            {subjectTypeLabel(entry.subjectType)}
                                        </span>
                                        {entry.subjectId && (
                                            <span className="mt-0.5 block break-all font-mono text-[10.5px] text-text-400">
                                                {entry.subjectId}
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className={wrapping}>
                                        {entry.organizationName ? (
                                            <span className="font-medium text-text-800">{entry.organizationName}</span>
                                        ) : (
                                            <span className="text-text-400">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-end align-top">
                                        <Button
                                            category="weak"
                                            size="small"
                                            icon
                                            aria-label={detailLabel}
                                            title={detailLabel}
                                            aria-expanded={isOpen}
                                            onClick={() => setOpenId(isOpen ? null : (entry.id ?? null))}
                                        >
                                            <EyeOutline size={16} />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                                {isOpen && (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="bg-background-weak">
                                            <div className="px-2 py-3">
                                                <MetadataDetail metadata={entry.metadata} />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
            {state}
        </>
    );
};

export default JournalTable;
